import { GATEWAY_OFFLINE_MINUTES, GATEWAY_WARNING_MINUTES } from './deviceType';
import type { DeviceStats } from '../types';

export const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  laptop: 'Laptop',
  raspberry_pi: 'RPI',
  windows_server: 'Windows',
  ubuntu: 'Ubuntu',
  vm: 'VM',
  unknown: 'Unknown',
};

export const PLATFORM_ORDER = ['laptop', 'raspberry_pi', 'windows_server', 'ubuntu', 'vm', 'unknown'];

export interface PlatformItem {
  key: string;
  platformKey: string;
  label: string;
  count: number;
}

export function getGatewayStatus(stat: DeviceStats): 'active' | 'offline' | 'unknown' {
  const diffMinutes = (Date.now() - new Date(stat.event_received_time).getTime()) / 60_000;
  if (diffMinutes > GATEWAY_OFFLINE_MINUTES) return 'offline';

  const status = (stat.stats?.system_status || '').toLowerCase();
  if (['active', 'online', 'healthy', 'running', 'ok'].some((v) => status.includes(v))) return 'active';
  if (['offline', 'down', 'inactive', 'error'].some((v) => status.includes(v))) return 'offline';
  return 'unknown';
}

export function getGatewayPlatform(stat: DeviceStats): string {
  return (stat.additional?.metadata?.platform ?? stat.additional_notes?.metadata?.platform) || 'unknown';
}

export function getComputeColor(percent?: number): string {
  if (percent == null) return 'rgba(255,255,255,0.3)';
  if (percent >= 90) return '#f87171';
  if (percent >= 70) return '#fbbf24';
  return '#34d399';
}

export interface TimeSinceResult {
  minutes: number;
  text: string;
  status: 'ok' | 'warning' | 'critical';
}

export function getTimeSinceLastEvent(stat: DeviceStats): TimeSinceResult {
  const diffMinutes = (Date.now() - new Date(stat.event_received_time).getTime()) / 60_000;

  let text: string;
  if (diffMinutes < 1) {
    text = 'Just now';
  } else if (diffMinutes < 60) {
    text = `${Math.floor(diffMinutes)} min ago`;
  } else if (diffMinutes < 1440) {
    text = `${Math.floor(diffMinutes / 60)} hours ago`;
  } else {
    text = `${Math.floor(diffMinutes / 1440)} days ago`;
  }

  const status: 'ok' | 'warning' | 'critical' =
    diffMinutes >= GATEWAY_OFFLINE_MINUTES ? 'critical' :
    diffMinutes >= GATEWAY_WARNING_MINUTES ? 'warning' :
    'ok';

  return { minutes: diffMinutes, text, status };
}

export function buildPlatformItems(counts: Record<string, number>, prefix: string): PlatformItem[] {
  return PLATFORM_ORDER.map((platform) => ({
    key: `${prefix}-${platform}`,
    platformKey: platform,
    label: PLATFORM_DISPLAY_NAMES[platform] || platform,
    count: counts[platform] || 0,
  }));
}

function escapeCsvValue(value: string): string {
  const safe = value ?? '';
  const escaped = safe.replace(/"/g, '""');
  return /[",\n]/.test(safe) ? `"${escaped}"` : escaped;
}

export function buildGatewayCsv(records: DeviceStats[]): string {
  const headers = ['client_id', 'site_id', 'uuid', 'platform', 'status', 'last_seen_local', 'last_seen_iso'];

  const rows = records.map((stat) => {
    const lastSeenDate = new Date(stat.event_received_time);
    return {
      client_id: stat.client_id || '',
      site_id: stat.site_id || '',
      uuid: stat.uuid || '',
      platform: getGatewayPlatform(stat),
      status: getGatewayStatus(stat) === 'active' ? 'online' : 'offline',
      last_seen_local: lastSeenDate.toLocaleString(),
      last_seen_iso: lastSeenDate.toISOString(),
    };
  });

  return [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((h) => escapeCsvValue(String(row[h as keyof typeof row] ?? ''))).join(',')
    ),
  ].join('\n');
}
